import coatOfArms from '../assets/coat-of-arms.png';

/**
 * The public landing page: what a visitor with no account sees at `/`.
 *
 * Until now that was the login form and nothing else, which told a stranger nothing and gave
 * the family nothing to look at. The history here is condensed from
 * `resources/Shanks_Family_History.txt`; more sections are expected over time, so each one is
 * a self-contained block in `SECTIONS` rather than a slab of markup.
 *
 * Signing in lives in the header menu (components/public-nav.js), not on this page, so the
 * page can grow without the form dominating it.
 */

const SECTIONS = [
    {
        title: 'A Border name',
        paragraphs: [
            `The Shanks name comes from the Border country between England and Scotland, the same
             ground that produced the Armstrongs, Grahams, Bells and Rutherfords. The family is
             believed to descend from the Strathclyde Britons, a mixed Gaelic and Celtic people
             whose territory ran from Lancashire north to the Clyde.`,
            `The first records place the family in Midlothian, where they were styled
             <em>Shank of that Ilk</em> — an ancient clan holding lands of the same name. In 1319
             Murdoch Shank, son of the first recorded chief, was granted the lands of Kinghorn in
             Fife by charter of King Robert the Bruce, for the clan's loyalty in his fight for the
             Scottish crown.`,
        ],
    },
    {
        title: 'One name, many spellings',
        paragraphs: [
            `Scribes and church officials wrote the name as they heard it, so the spelling changed
             constantly — sometimes between a father and his own son. The archives hold Shank,
             Shanke, Schanke, Shankis, Schankis, Shanx and Synkis, all the same family.`,
        ],
    },
    {
        title: 'In the records',
        paragraphs: [
            `Thomas Shankis witnessed a charter at the Castle of Cumnock in 1426, and James Schankis
             did the same in 1489. Ninian Shankis was a prior of the Dominican order in 1490, the
             year Stene Shankis appears to have started spelling himself Synkis. Henry Shank sat in
             the Scottish Parliament for Kinghorn in 1643.`,
            `By then the clan had scattered to Wigton, Leith, Futtie and Parmure, and had acquired
             estates at Castlerig in Norfolk. Admiral John Schank, who died in 1823, is the most
             notable of the family from that period.`,
        ],
    },
    {
        title: 'The border code',
        paragraphs: [
            `In 1246 six chiefs from each side of the border met at Carlisle and agreed laws unlike
             any others in Britain. Refusing to help a neighbour recover stolen property, livestock
             or family — the "Hot Trod" — was a graver offence than the theft itself, and could see
             a man hanged on the spot without trial.`,
            `When the crowns united under James I in 1603, those unruly border clans were broken up
             deliberately and banished to England, northern Scotland and Ireland. Some were outlawed
             straight to the colonies.`,
        ],
    },
    {
        title: 'Ulster and the New World',
        paragraphs: [
            `During the plantation of Northern Ireland, 21 heads of Shanks families settled in
             Ulster, ten of them in Antrim and seven in County Down. Many became proudly Irish, but
             life there was hard, and they looked further west.`,
            `They crossed on the "White Sails" — ships like the Hector, the Rambler and the Dove —
             on voyages that could lose a third of their passengers to dysentery, cholera, smallpox
             and typhoid. John Shanks reached Virginia in 1650, Stephen Shank Barbados in 1663,
             Mathew Shanks Charles Town in 1766, and John George Shank Pennsylvania in 1770. Those
             who stayed loyal to the Crown during the American War of Independence moved north into
             Canada as United Empire Loyalists.`,
        ],
    },
    {
        title: 'The arms and the motto',
        paragraphs: [
            `The oldest grant of arms found for the name is a red background with a silver
             horizontal stripe bearing a hawk's lure, a five-leafed clover above it and a hawk's leg
             below. The crest is an eagle.`,
            `The motto is <strong>Spero</strong> — Latin for "I hope", from <em>sperare</em>, to
             hope.`,
        ],
    },
];

export function renderLanding() {
    document.getElementById('app').innerHTML = `
        <div class="container landing">
            <header class="landing-hero">
                <img class="landing-arms" src="${coatOfArms}" width="280" height="372"
                     alt="The Shanks coat of arms: a silver band bearing a hawk's lure on a red shield, with a five-leafed clover above and a hawk's leg below">
                <h1 class="landing-title">Shanks — An Ancient Name</h1>
                <p class="landing-motto">Spero — <span>I hope</span></p>
                <p class="landing-lede">A private portal for the Shanks family.</p>
            </header>

            ${SECTIONS.map((section) => `
            <section class="landing-section">
                <h2>${section.title}</h2>
                ${section.paragraphs.map((text) => `<p>${text}</p>`).join('')}
            </section>`).join('')}

            <footer class="landing-footer">
                <p>
                    Family members can <a href="/login" data-link>sign in</a> for photos, events and
                    the family calendar. Accounts are set up by a family administrator — there is no
                    public sign-up.
                </p>
            </footer>
        </div>`;
}
